import { Badge, Icon, Text, Heading } from "@oysterhr/ui"

import { useTranslation } from "react-i18next"
import { PaymentMethodPurposeEnumType, B2BPaymentMethod, CorePaymentMethod } from "@oysterhr/schema"
import { PaymentMethodEnumType } from "~/app/utils/constants"
import { PaymentMethodStatusBadge } from "../../../../components/PaymentMethodStatusBadge"
import { isFtePaymentMethod } from "../../utils"

const setupNameMap = {
  [PaymentMethodEnumType.CUSTOMER_BANK_ACCOUNT_SEPA]: "SEPA Direct Debit",
  [PaymentMethodEnumType.CUSTOMER_BANK_ACCOUNT_ACH]: "ACH Direct Debit",
  [PaymentMethodEnumType.STRIPE_SEPA]: "SEPA Direct Debit",
  [PaymentMethodEnumType.STRIPE_ACH]: "ACH Direct Debit",
  [PaymentMethodEnumType.STRIPE_BACS]: "BACS",
  [PaymentMethodEnumType.STRIPE_PAD]: "PAD",
}

interface NewCompanyPaymentMethodCardLayoutProps {
  paymentMethod?: CorePaymentMethod | B2BPaymentMethod
  children: React.ReactNode
}

// This is a duplication of CardLayout to avoid changes in current ways they're displayed until the release of the new payment setup
export const NewCompanyPaymentMethodCardLayout = ({
  paymentMethod,
  children,
}: NewCompanyPaymentMethodCardLayoutProps) => {
  const { t } = useTranslation("company_interface/CompanyPaymentMethodsPage/CardLayout")

  if (!paymentMethod) return null

  const paymentHeader =
    paymentMethod.purpose === PaymentMethodPurposeEnumType.CollectionAndPassing
      ? t("header.contractorAndFte")
      : isFtePaymentMethod(paymentMethod)
        ? t("header.fte")
        : t("header.contractor")
  const isAch =
    paymentMethod.__typename === PaymentMethodEnumType.STRIPE_ACH ||
    paymentMethod.__typename === PaymentMethodEnumType.CUSTOMER_BANK_ACCOUNT_ACH

  return (
    <div
      data-testid={paymentMethod.id}
      className="h-full flex-col justify-between rounded-lg border border-gray-200 p-4"
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex">
          <Icon className="text-black" size="sm" name={"BankIcon"} />
          <Text className="ml-2 whitespace-nowrap font-semibold">{paymentHeader}</Text>
        </div>
        <div>
          <PaymentMethodStatusBadge paymentMethod={paymentMethod} />
        </div>
      </div>

      <div className="w-2/3">
        <div className="mt-8 flex gap-8">
          <div className="flex flex-col">
            <Text className="text-gray-400" size="small">
              {t("currency")}
            </Text>
            <Heading as="h4">{paymentMethod?.currencyCode}</Heading>
          </div>
          <div className="flex flex-col">
            <Text className="whitespace-nowrap text-gray-400" size="small">
              {t("accountNumber")}
            </Text>
            <Heading as="h4">{`**${paymentMethod?.accountNumber.slice(-4)}`}</Heading>
          </div>
          {isAch && (
            <div className="flex flex-col">
              <Text className="whitespace-nowrap text-gray-400" size="small">
                {t("routing.routingCode")}
              </Text>
              <Heading as="h4">{paymentMethod?.routingNumber}</Heading>
            </div>
          )}
        </div>
      </div>
      <div className="mt-6 flex items-center justify-between">
        <Badge variation="gray" isOutline size="sm">
          {getPaymentMethodType(paymentMethod)}
        </Badge>
        {children}
      </div>
    </div>
  )
}

const getPaymentMethodType = (paymentMethod?: CorePaymentMethod | B2BPaymentMethod) => {
  if (!paymentMethod?.__typename) return
  return setupNameMap[paymentMethod.__typename]
}
